import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type AuditLogEntry,
  type AuditLogListQuery,
} from '@/api/v2Client';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Spinner } from '@/components/dashboard/Spinner';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { Pagination } from '@/components/dashboard/Pagination';
import { formatDateTime } from '@/lib/format';
import { PermissionDenied } from '@/pages/PermissionDenied';

// Phase 5.27 — V1-parity audit log viewer.
//
// Surface area:
//   - Filter sidebar (collapsible)
//   - Date range presets (Today, Last 7 days, Last 30 days, Custom)
//   - Action dropdown populated from /api/v1/audit-log/actions (with a
//     hard-coded fallback for offline-test runs where the API mock omits it)
//   - Target type dropdown
//   - Actor email filter (free text, lowercased)
//   - Free-text search across Action + Before/After (debounced 300ms)
//   - Sortable column headers (OccurredAt / Action / ActorEmail / TargetType)
//   - Per-row expand: Before/After diff + IP / User-Agent
//   - Server-side pagination (50 per page default)
//   - CSV export of the current filter set (download via anchor)

const PAGE_SIZE = 50;
const FREE_TEXT_DEBOUNCE_MS = 300;

const TARGET_TYPE_OPTIONS = [
  'Submissions',
  'Documents',
  'ValidationRules',
  'CompanyProfiles',
  'AdminForms',
  'Users',
  'AuditLog',
  'SpecFiles',
  'Roles',
  'Settings',
  'Prompts',
] as const;

// Fallback action list — used if /audit-log/actions hasn't responded yet
// or returns an error. Same set the pre-5.27 page hard-coded.
const FALLBACK_ACTION_OPTIONS = [
  'read',
  'write',
  'delete',
  'sign',
  'manage',
  'auth.bootstrap-admin',
  'user.role.assign',
  'user.role.revoke',
  'user.delete',
  'document.classification.approve',
  'document.placement.change',
  'submission.import.commit',
] as const;

type DateRangePreset = 'today' | 'last-7-days' | 'last-30-days' | 'custom' | '';

interface FilterDraft {
  actorEmail: string;
  targetType: string;
  /**
   * Phase 5.X-audit-log-admin-viewer — Target ID (e.g. a SubmissionId or
   * DocumentId). Sent to the backend as `?targetId=…`. Combine with
   * `targetType` to scope the log to a single resource instance.
   */
  targetId: string;
  action: string;
  q: string;
  preset: DateRangePreset;
  fromCustom: string; // datetime-local string
  toCustom: string;
}

const EMPTY_DRAFT: FilterDraft = {
  actorEmail: '',
  targetType: '',
  targetId: '',
  action: '',
  q: '',
  preset: '',
  fromCustom: '',
  toCustom: '',
};

interface ResolvedDateRange {
  fromDate: string | null;
  toDate: string | null;
}

/**
 * Turn a preset + the custom-range inputs into ISO strings the backend
 * accepts. `today` = midnight-UTC to now; `last-7-days` = 7×24h ago to
 * now; etc. `custom` reads the two datetime-local inputs (treated as
 * local time, converted to ISO).
 */
function resolveDateRange(draft: FilterDraft, now: Date = new Date()): ResolvedDateRange {
  if (draft.preset === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { fromDate: start.toISOString(), toDate: now.toISOString() };
  }
  if (draft.preset === 'last-7-days') {
    return {
      fromDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      toDate: now.toISOString(),
    };
  }
  if (draft.preset === 'last-30-days') {
    return {
      fromDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      toDate: now.toISOString(),
    };
  }
  if (draft.preset === 'custom') {
    return {
      fromDate: draft.fromCustom ? new Date(draft.fromCustom).toISOString() : null,
      toDate: draft.toCustom ? new Date(draft.toCustom).toISOString() : null,
    };
  }
  return { fromDate: null, toDate: null };
}

type SortDirection = 'asc' | 'desc';
type SortColumn = 'OccurredAt' | 'Action' | 'ActorEmail' | 'TargetType';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

const DEFAULT_SORT: SortState = { column: 'OccurredAt', direction: 'desc' };

const tdStyle: CSSProperties = { padding: '10px 8px', verticalAlign: 'top', fontSize: 13 };
const sortableHeaderStyle: CSSProperties = {
  ...tdStyle,
  cursor: 'pointer',
  userSelect: 'none',
};
const inputStyle: CSSProperties = {
  display: 'block',
  marginTop: 4,
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
};

export function AuditLogPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('AuditLog', 'read');
  // Phase 5.27a — operator-triggered retention cleanup. Same admin role
  // (AuditLog:write) as the backend route. When false, the button below
  // is hidden — read-only auditors don't see it.
  const canWrite = perms.hasPermission('AuditLog', 'write');

  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<AuditLogListQuery>({});
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionOptions, setActionOptions] = useState<readonly string[]>(
    FALLBACK_ACTION_OPTIONS,
  );

  // Phase 5.27a — retention-cleanup button state.
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  // D-29 — what a real run WOULD do, measured by a dry run, held while the
  // operator decides. Null means no dialog is open and nothing is pending.
  //
  // T-08 adds `previewToken`: the server's authority to perform exactly this
  // run. Held here and sent back on confirm, so the rows moved are the rows
  // that were counted.
  const [pendingCleanup, setPendingCleanup] = useState<{
    moved: number;
    cutoffDate: string;
    previewToken?: string;
  } | null>(null);

  // Debounced free-text q. The draft has the live value; this is the
  // applied value that goes into the fetch.
  const debounceTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  // Build the AuditLogListQuery sent to the backend from the applied
  // filters + sort + page. Pulled into useMemo so the fetch effect re-runs
  // when (and only when) one of these inputs changes.
  const query = useMemo<AuditLogListQuery>(
    () => ({
      ...applied,
      page,
      pageSize: PAGE_SIZE,
      sort: `${sort.column}:${sort.direction}`,
    }),
    [applied, page, sort],
  );

  // Fetch the action vocabulary once on mount (gated on permission). The
  // fallback is already populated so a slow / failing response doesn't
  // break the page.
  useEffect(() => {
    if (!canRead) return;
    v2.auditLog
      .listActions()
      .then((res) => {
        if (res.actions.length > 0) setActionOptions(res.actions);
      })
      .catch(() => {
        // Quietly keep the fallback. Filed under "soft failure".
      });
  }, [canRead]);

  // Fetch on filter / page / sort change.
  useEffect(() => {
    if (!canRead) return;
    setEntries(null);
    setError(null);
    v2.auditLog
      .list(query)
      .then((res) => {
        setEntries(res.entries);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        const parsed = parseApiErrorBody(err);
        const message =
          parsed?.message ??
          (err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load audit log');
        setError(message);
        setEntries([]);
      });
  }, [canRead, query]);

  // Debounced free-text q. Whenever the user types into the search box,
  // wait 300ms before pushing it into `applied`.
  // T07-D15 — only when the text actually differs from the applied filter.
  // This effect used to arm on mount with an empty, unchanged box and call
  // setPage(1) 300ms later, so a Next click inside that window fetched
  // page 2 and was then bounced back to page 1 with a third request.
  const appliedQ = applied.q ?? '';
  useEffect(() => {
    if (debounceTimer.current) globalThis.clearTimeout(debounceTimer.current);
    if (draft.q.trim() === appliedQ) return;
    debounceTimer.current = globalThis.setTimeout(() => {
      const trimmed = draft.q.trim();
      setApplied((prev) => {
        // No change → don't churn the query.
        if ((prev.q ?? '') === trimmed) return prev;
        const next = { ...prev };
        if (trimmed) next.q = trimmed;
        else delete next.q;
        return next;
      });
      setPage(1);
    }, FREE_TEXT_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) globalThis.clearTimeout(debounceTimer.current);
    };
  }, [draft.q, appliedQ]);

  const handleApply = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const next: AuditLogListQuery = {};
    if (draft.actorEmail.trim()) next.actorEmail = draft.actorEmail.trim().toLowerCase();
    if (draft.targetType) next.targetType = draft.targetType;
    if (draft.targetId.trim()) next.targetId = draft.targetId.trim();
    if (draft.action) next.action = draft.action;
    if (draft.q.trim()) next.q = draft.q.trim();
    const range = resolveDateRange(draft);
    if (range.fromDate) next.fromDate = range.fromDate;
    if (range.toDate) next.toDate = range.toDate;
    setApplied(next);
    setPage(1);
  };

  const handleClear = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setApplied({});
    setPage(1);
    setSort(DEFAULT_SORT);
  }, []);

  const handleSort = (col: SortColumn): void => {
    setSort((prev) =>
      prev.column === col
        ? { column: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column: col, direction: 'desc' },
    );
    setPage(1);
  };

  // Phase 5.27a — manual cleanup trigger. Hits the backend's
  // POST /api/v1/admin/audit-log/retention-cleanup which is gated on
  // `AuditLog:write`.
  //
  // D-29: this used to default to a NON-dry run, on the reasoning that the
  // button was for incident response and ops could preview with curl. The
  // result was that one click on "Run retention cleanup now" archived rows
  // with no confirmation and no indication of how many. The preview is no
  // longer somewhere else — `handleRunClick` below does the dry run first
  // and shows the number before anything moves. This function stays as the
  // single place that talks to the endpoint, for both kinds of run.
  const handleRetentionCleanup = useCallback(
    async (dryRun: boolean, previewToken?: string) => {
      setCleanupBusy(true);
      setCleanupMessage(null);
      setCleanupError(null);
      try {
        const res = await v2.auditLog.runRetentionCleanup({
          dryRun,
          ...(previewToken !== undefined && { previewToken }),
        });
        const {
          moved,
          batches,
          truncated,
          dryRun: wasDryRun,
          activeRetentionDays,
        } = res;
        const verb = wasDryRun ? 'Would move' : 'Moved';
        const truncatedNote = truncated
          ? ' (capped at this run’s batch limit; run again to drain the rest)'
          : '';
        setCleanupMessage(
          `${verb} ${moved} row${moved === 1 ? '' : 's'} in ${batches} batch${batches === 1 ? '' : 'es'} ` +
            `to archive (retention window: ${activeRetentionDays} days).${truncatedNote}`,
        );
      } catch (err) {
        if (err instanceof ApiError) {
          const parsed = parseApiErrorBody(err);
          setCleanupError(parsed?.message ?? err.message);
        } else {
          setCleanupError(err instanceof Error ? err.message : 'Cleanup failed');
        }
      } finally {
        setCleanupBusy(false);
      }
    },
    [],
  );

  /**
   * D-29 — "Run retention cleanup now" starts with a DRY RUN.
   *
   * The dry run is the same endpoint with `dryRun: true`; the server does
   * the work inside a transaction and rolls it back, so the count it
   * reports is real rather than estimated. Only after the operator agrees
   * to that number does anything move.
   */
  const handleRunClick = useCallback(async (): Promise<void> => {
    setCleanupBusy(true);
    setCleanupMessage(null);
    setCleanupError(null);
    try {
      const res = await v2.auditLog.runRetentionCleanup({ dryRun: true });
      setPendingCleanup({
        moved: res.moved,
        cutoffDate: res.cutoffDate,
        ...(res.previewToken !== undefined && { previewToken: res.previewToken }),
      });
    } catch (err) {
      // A failed preview opens no dialog: there is nothing to confirm.
      if (err instanceof ApiError) {
        const parsed = parseApiErrorBody(err);
        setCleanupError(parsed?.message ?? err.message);
      } else {
        setCleanupError(err instanceof Error ? err.message : 'Cleanup preview failed');
      }
    } finally {
      setCleanupBusy(false);
    }
  }, []);

  /**
   * The operator agreed to the number they were shown — and the token proves
   * it to the server, which refuses a real run without one (409
   * StalePreview).
   */
  const confirmCleanup = useCallback(async (): Promise<void> => {
    const token = pendingCleanup?.previewToken;
    setPendingCleanup(null);
    await handleRetentionCleanup(false, token);
  }, [handleRetentionCleanup, pendingCleanup]);

  /**
   * D-30 — download the export as an authenticated request, then hand the
   * blob to the browser. Not an anchor: see v2Client.downloadExport.
   */
  const handleExport = useCallback(async (): Promise<void> => {
    setExportBusy(true);
    setExportError(null);
    try {
      const q: AuditLogListQuery = { ...applied, sort: `${sort.column}:${sort.direction}` };
      const blob = await v2.auditLog.downloadExport(q);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      } finally {
        // Revoked immediately: the click has already handed the bytes over,
        // and a leaked object URL pins the whole blob in memory.
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const parsed = parseApiErrorBody(err);
        setExportError(parsed?.message ?? err.message);
      } else {
        setExportError(err instanceof Error ? err.message : 'Export failed');
      }
    } finally {
      setExportBusy(false);
    }
  }, [applied, sort]);

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead) return <PermissionDenied />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sortIndicator = (col: SortColumn): string =>
    sort.column === col ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <section>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Audit Log</h1>
        <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
          Tamper-evident record of all authenticated actions in the system — protected by database-level write triggers (21 CFR Part 11 §11.10).
        </p>
      </div>

      <details
        open
        style={{
          marginBottom: 16,
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: 12,
          background: 'var(--surface)',
        }}
        data-testid="audit-filters"
      >
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Filters</summary>
        <form
          onSubmit={handleApply}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginTop: 12,
            alignItems: 'end',
          }}
        >
          {/* Free-text search — debounced, applies without "Apply" click. */}
          <label style={{ display: 'block', fontSize: 12, gridColumn: '1 / -1' }}>
            Search (action, before/after JSON)
            <input
              type="search"
              value={draft.q}
              onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
              aria-label="Free-text search filter"
              data-testid="audit-search"
              placeholder="e.g. approve, reason, reviewer@…"
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block', fontSize: 12 }}>
            Actor email
            <input
              type="email"
              value={draft.actorEmail}
              onChange={(e) => setDraft((d) => ({ ...d, actorEmail: e.target.value }))}
              aria-label="Actor email filter"
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block', fontSize: 12 }}>
            Target type
            <select
              value={draft.targetType}
              onChange={(e) => setDraft((d) => ({ ...d, targetType: e.target.value }))}
              aria-label="Target type filter"
              style={inputStyle}
            >
              <option value="">All target types</option>
              {TARGET_TYPE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          {/* Phase 5.X-audit-log-admin-viewer — Target ID filter. */}
          <label style={{ display: 'block', fontSize: 12 }}>
            Target ID (e.g. Submission ID)
            <input
              type="text"
              value={draft.targetId}
              onChange={(e) => setDraft((d) => ({ ...d, targetId: e.target.value }))}
              aria-label="Target ID filter"
              data-testid="audit-target-id"
              placeholder="Paste a submission or document ID"
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block', fontSize: 12 }}>
            Action
            <select
              value={draft.action}
              onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
              aria-label="Action filter"
              style={inputStyle}
            >
              <option value="">All actions</option>
              {actionOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'block', fontSize: 12 }}>
            Date range
            <select
              value={draft.preset}
              onChange={(e) =>
                setDraft((d) => ({ ...d, preset: e.target.value as DateRangePreset }))
              }
              aria-label="Date range preset"
              data-testid="audit-date-preset"
              style={inputStyle}
            >
              <option value="">Any time</option>
              <option value="today">Today</option>
              <option value="last-7-days">Last 7 days</option>
              <option value="last-30-days">Last 30 days</option>
              <option value="custom">Custom range…</option>
            </select>
          </label>

          {draft.preset === 'custom' && (
            <>
              <label style={{ display: 'block', fontSize: 12 }}>
                From
                <input
                  type="datetime-local"
                  value={draft.fromCustom}
                  onChange={(e) => setDraft((d) => ({ ...d, fromCustom: e.target.value }))}
                  aria-label="From date filter"
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'block', fontSize: 12 }}>
                To
                <input
                  type="datetime-local"
                  value={draft.toCustom}
                  onChange={(e) => setDraft((d) => ({ ...d, toCustom: e.target.value }))}
                  aria-label="To date filter"
                  style={inputStyle}
                />
              </label>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="submit"
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Apply filters
            </button>
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: 'none',
                color: 'var(--text-muted)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                textDecoration: 'underline',
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={exportBusy}
              aria-label="Export filtered audit log as CSV"
              data-testid="audit-export-csv"
              style={{
                marginLeft: 'auto',
                background: 'none',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: exportBusy ? 'not-allowed' : 'pointer',
                opacity: exportBusy ? 0.6 : 1,
              }}
            >
              {exportBusy ? 'Preparing…' : 'Export CSV ↓'}
            </button>
          </div>
          {exportError !== null && (
            <div
              role="alert"
              data-testid="audit-export-error"
              style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}
            >
              {exportError}
            </div>
          )}
        </form>
      </details>

      {entries === null && !error && <Spinner caption="Loading audit log…" />}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Phase 5.13f — always render the table with column headers visible,
          even when entries is empty. Unlike pages with client-side filters
          we can't compute filtered.length here (the backend does the
          filtering), so we infer "filter-excluded vs zero-loaded" from
          whether the applied query has any filter keys set. With no filter
          keys + empty result, the underlying audit log is genuinely empty;
          with filter keys + empty result, the operator's filter combo
          excluded every row. The 6 columns are spanned by colSpan=6. */}
      {entries !== null && (
        <>
          <ResponsiveTable
            data-testid="audit-log-table"
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                <th
                  style={sortableHeaderStyle}
                  aria-sort={sort.column === 'OccurredAt' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  data-testid="audit-sort-OccurredAt"
                >
                  <button className="table-sort" type="button" onClick={() => handleSort('OccurredAt')}>When{sortIndicator('OccurredAt')}</button>
                </th>
                <th
                  style={sortableHeaderStyle}
                  aria-sort={sort.column === 'ActorEmail' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  data-testid="audit-sort-ActorEmail"
                >
                  <button className="table-sort" type="button" onClick={() => handleSort('ActorEmail')}>Actor{sortIndicator('ActorEmail')}</button>
                </th>
                <th
                  style={sortableHeaderStyle}
                  aria-sort={sort.column === 'Action' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  data-testid="audit-sort-Action"
                >
                  <button className="table-sort" type="button" onClick={() => handleSort('Action')}>Action{sortIndicator('Action')}</button>
                </th>
                <th
                  style={sortableHeaderStyle}
                  aria-sort={sort.column === 'TargetType' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  data-testid="audit-sort-TargetType"
                >
                  <button className="table-sort" type="button" onClick={() => handleSort('TargetType')}>Target type{sortIndicator('TargetType')}</button>
                </th>
                <th style={tdStyle}>Target id</th>
                <th style={tdStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr data-testid="audit-log-empty-row">
                  <td
                    colSpan={6}
                    style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: 13,
                    }}
                  >
                    {Object.keys(applied).length === 0 ? (
                      <>
                        <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">
                          📋
                        </div>
                        <div
                          style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}
                        >
                          No audit entries yet
                        </div>
                        <div>
                          The audit log is tenant-scoped; entries appear here
                          as users sign in and perform actions. Brand-new tenants
                          will see this empty state until their first action.
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}
                        >
                          No audit entries match the current filters
                        </div>
                        <div>
                          Showing 0 of {total} entr{total === 1 ? 'y' : 'ies'}.
                          Reset filters above to see all entries.
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                entries.map((e) => {
                const isExpanded = expandedId === e.Id;
                return (
                  <tr
                    key={e.Id}
                    style={{ borderBottom: '1px solid var(--border)' }}
                    data-testid={`audit-row-${e.Id}`}
                  >
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                      {formatDateTime(e.OccurredAt)}
                    </td>
                    <td style={tdStyle} title={e.ActorId}>
                      {e.ActorEmail ?? <em>system</em>}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{e.Action}</td>
                    <td style={tdStyle}>{e.TargetType ?? '—'}</td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={e.TargetId ?? ''}
                    >
                      {e.TargetId ?? '—'}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : e.Id)}
                        aria-label="Toggle row details"
                        data-testid={`audit-expand-${e.Id}`}
                        style={{
                          background: 'none',
                          border: '1px solid var(--border)',
                          borderRadius: 4,
                          padding: '2px 8px',
                          cursor: 'pointer',
                          fontSize: 11,
                        }}
                      >
                        {isExpanded ? 'Hide' : 'View'}
                      </button>
                      {isExpanded && (
                        <div
                          data-testid={`audit-details-${e.Id}`}
                          style={{
                            marginTop: 6,
                            padding: 8,
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            fontSize: 11,
                            maxWidth: 480,
                          }}
                        >
                          {(e.Before != null || e.After != null) && (
                            <pre
                              style={{
                                marginTop: 0,
                                marginBottom: 6,
                                overflow: 'auto',
                                maxHeight: 200,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {`Before: ${JSON.stringify(e.Before, null, 2)}\n\nAfter:  ${JSON.stringify(e.After, null, 2)}`}
                            </pre>
                          )}
                          <dl
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'max-content 1fr',
                              rowGap: 2,
                              columnGap: 12,
                              margin: 0,
                            }}
                          >
                            <dt style={{ color: 'var(--text-muted)' }}>Actor ID</dt>
                            <dd style={{ margin: 0, fontFamily: 'monospace' }}>{e.ActorId}</dd>
                            <dt style={{ color: 'var(--text-muted)' }}>IP</dt>
                            <dd style={{ margin: 0, fontFamily: 'monospace' }}>
                              {e.IpAddress ?? '—'}
                            </dd>
                            <dt style={{ color: 'var(--text-muted)' }}>User-Agent</dt>
                            <dd style={{ margin: 0, wordBreak: 'break-all' }}>
                              {e.UserAgent ?? '—'}
                            </dd>
                            <dt style={{ color: 'var(--text-muted)' }}>Request ID</dt>
                            <dd style={{ margin: 0, fontFamily: 'monospace' }}>
                              {e.RequestId ?? '—'}
                            </dd>
                          </dl>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </ResponsiveTable>
          {entries.length > 0 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      {/* Phase 5.27a — operator-only retention-cleanup trigger. Only renders
          for users with AuditLog:write. Pairs with the nightly
          `audit-log-retention.yml` workflow — this button is for
          incident-response use when ops want to drain the active table
          immediately. */}
      {canWrite && (
        <div
          data-testid="audit-retention-panel"
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              disabled={cleanupBusy}
              onClick={() => void handleRetentionCleanup(true)}
              data-testid="audit-retention-dryrun"
              style={{
                background: 'none',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                cursor: cleanupBusy ? 'not-allowed' : 'pointer',
              }}
            >
              Dry-run retention cleanup
            </button>
            <button
              type="button"
              disabled={cleanupBusy}
              onClick={() => void handleRunClick()}
              data-testid="audit-retention-run"
              style={{
                background: 'var(--accent)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: cleanupBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {cleanupBusy ? 'Running…' : 'Run retention cleanup now'}
            </button>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              Moves audit entries older than the org’s `auditLog.activeRetentionDays`
              setting (default 7 years) into the archive table.
            </span>
            {pendingCleanup !== null && (
              <ConfirmModal
                title={
                  pendingCleanup.moved > 0 ? 'Archive audit entries?' : 'Nothing to archive'
                }
                message={
                  pendingCleanup.moved > 0
                    ? `${pendingCleanup.moved} ${
                        pendingCleanup.moved === 1 ? 'entry' : 'entries'
                      } older than ${pendingCleanup.cutoffDate.slice(0, 10)} will be archived. ` +
                      'They move out of the active audit log into the archive table; nothing is deleted.'
                    : `Nothing to archive — no audit entries are older than ${pendingCleanup.cutoffDate.slice(0, 10)}.`
                }
                confirmLabel={`Archive ${pendingCleanup.moved} ${
                  pendingCleanup.moved === 1 ? 'entry' : 'entries'
                }`}
                cancelLabel={pendingCleanup.moved > 0 ? 'Cancel' : 'Close'}
                variant="destructive"
                // Nothing to agree to when the count is zero, so no button
                // to agree with.
                hideConfirm={pendingCleanup.moved === 0}
                {...(pendingCleanup.moved > 0 && {
                  // T-08 — the phrase to type is the COUNT, not a fixed word:
                  // typing the number forces the operator to read the figure
                  // they are agreeing to. A constant like "ARCHIVE" can be
                  // typed from memory.
                  typeToConfirm: {
                    expected: String(pendingCleanup.moved),
                    label: `Type ${pendingCleanup.moved} to confirm`,
                  },
                })}
                isLoading={cleanupBusy}
                onCancel={() => setPendingCleanup(null)}
                onConfirm={() => void confirmCleanup()}
              />
            )}
          </div>
          {cleanupMessage && (
            <div
              role="status"
              data-testid="audit-retention-result"
              style={{ color: 'var(--text)', fontSize: 12 }}
            >
              {cleanupMessage}
            </div>
          )}
          {cleanupError && (
            <div
              role="alert"
              data-testid="audit-retention-error"
              style={{ color: 'var(--red)', fontSize: 12 }}
            >
              {cleanupError}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
